import { useState, useCallback } from 'react';
import { REDEEM_CODE_PROXY_URL } from '../config';

const errorMap : any = {
  CAPCHA_FAIL: {
    en: "The text you entered didn't match the picture. Please try again.",
    zh: "你輸入的文字與圖片不符。請再試一次。"
  },
  SYSTEM_ERROR: {
    en: "Sorry! Something's not working quite right. Please try again.",
    zh: "抱歉!系統現正繁忙，請稍後再試。"
  },
  COUPONCODE_INVALID_DV: {
    en: "Sorry! This code does not apply. To know more how to use “DV” discount code to redeem, please visit <a href='https://www.nowtv.now.com/support/how-to-redeem-code' target='_blank'>here</a>​",
    zh: "抱歉！此代碼不適用。了解如何使用「DV」字首折扣優惠代碼換領，請前往<a href='https://www.nowtv.now.com/support/how-to-redeem-code' target='_blank'>此頁面</a>"
  }
};

export const getPromoCodeFromUrl = (): string => {
  if (window.location.href.indexOf("?") > 0) {
    const params = window.location.search.replace("?", "").split("&");
    for (const d of params) {
      const pair = d.split('=');
      if (pair[0].toLowerCase() === "promocode") {
        return pair[1] || '';
      }
    }
  }
  return '';
};

export const getCaptchaImageUrl = (): string => {
  const baseCaptchaUrl = 'https://account.now.com/MyAccount/captcha/captcha.png';
  return `${baseCaptchaUrl}?t=${new Date().getTime()}`;
};

export const refreshCaptchaUrl = (currentSrc: string): string => {
  const baseUrl = currentSrc.split('?t=')[0] || 'https://account.now.com/MyAccount/captcha/captcha.png';
  return `${baseUrl}?t=${new Date().getTime()}`;
};

export const getErrorMessage = (code: string, lang: string): string => {
  return errorMap[code as keyof typeof errorMap]?.[lang] || errorMap.SYSTEM_ERROR[lang];
};

/**
 * Validates captcha via PHP proxy using GET to avoid CORS issues.
 */
export const validateCaptchaViaProxy = async (captchaCode: string, redemptionCode: string) => {
  const proxyUrl = `${REDEEM_CODE_PROXY_URL}?captcha=${encodeURIComponent(captchaCode)}&redemptionCode=${encodeURIComponent(redemptionCode)}` || `/api/redeem-code-proxy.php?captcha=${encodeURIComponent(captchaCode)}&redemptionCode=${encodeURIComponent(redemptionCode)}`;

  try {
    const response = await fetch(proxyUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return typeof data.result === 'string' ? JSON.parse(data.result) : data;
  } catch (error) {
    console.error("Proxy Captcha Validation Error:", error);
    throw error;
  }
};

export const useCouponValidation = (lang: string) => {
  const [redemptionCodeError, setRedemptionCodeError] = useState<string>('');
  const [captchaError, setCaptchaError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const validateRedemptionCode = useCallback((code: string) => {
    let error = '';
    if (code.startsWith('DV')) {
      error = getErrorMessage('COUPONCODE_INVALID_DV', lang);
    }
    setRedemptionCodeError(error);
    if (code === '') {
      setRedemptionCodeError('');
    }
    return error === '';
  }, [lang]);

  const validateCouponCode = useCallback(async (
    redemptionCode: string,
    captcha: string,
    refreshCaptcha: () => void,
    lang: string
  ): Promise<void> => {
    if (isSubmitting) return;

    setRedemptionCodeError('');
    setCaptchaError('');
    setIsSubmitting(true);

    try {
      const data = await validateCaptchaViaProxy(captcha, redemptionCode);
      console.log("validateCaptchaViaProxy response",data);
      if (data.validateResult === "ok") {
        // Success: Redirect to redeem-code with promocode as requested
        window.location.href = `/redeem-code?promocode=${redemptionCode}`;
      } else if(data.validateResult === "fail"){
        // capcha fail
        const errorMsg = getErrorMessage('CAPCHA_FAIL', lang);
        setCaptchaError(errorMsg);
        refreshCaptcha();
      } else {
        // other error
        const otherErrorMsg = getErrorMessage('SYSTEM_ERROR', lang);
        setCaptchaError(otherErrorMsg);
        refreshCaptcha();
      }
    } catch (error) {
      const systemErrorMsg = getErrorMessage('SYSTEM_ERROR', lang);
      setCaptchaError(systemErrorMsg);
      refreshCaptcha();
      console.error('Captcha validation process error:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, lang]);

  return {
    redemptionCodeError,
    captchaError,
    isSubmitting,
    setIsSubmitting,
    setRedemptionCodeError,
    validateRedemptionCode,
    validateCouponCode,
    clearErrors: () => {
      setRedemptionCodeError('');
      setCaptchaError('');
    }
  };
};
